import {Component, OnInit, ChangeDetectionStrategy} from '@angular/core';
import { NgOptimizedImage } from "@angular/common";
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {LoaderService} from '../../../../../services/loader/loader.service';
import {SnackbarService} from '../../../../../services/snackbar/snackbar.service';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {InputSwitchComponent} from '../../../../layout/input-switch/input-switch.component';
import {ProductService} from '../../../../../services/product/product.service';
import {forkJoin} from 'rxjs';
import {Product} from '../../../../../models/product';
import {Country} from '../../../../../models/country';
import {CountryService} from '../../../../../services/country/country.service';
import {IsGranted} from '../../../../../decorator/is-granted';
import {AuthService} from '../../../../../services/auth/auth.service';
import {MetaTitle} from '../../../../../decorator/meta-title';
import {Site} from '../../../../../models/site/site';
import {SiteService} from '../../../../../services/site/site.service';
import {PartnerService} from '../../../../../services/partner/partner.service';
import {Partner} from '../../../../../models/partner/partner';
import {LangService} from '../../../../../services/language/lang.service';
import {DropdownSelectComponent} from '../../../../layout/dropdown-select/dropdown-select.component';
import {
  DropdownSelectOptionComponent
} from '../../../../layout/dropdown-select-option/dropdown-select-option.component';
import {Offer} from '../../../../../models/offer';
import {OfferService} from '../../../../../services/offer/offer.service';
import {
  ProductPrioritySelectorComponent
} from '../../../../layout/product-priority-selector/product-priority-selector.component';
import {Language} from '../../../../../models/language';

@Component({
  selector: 'app-site-form',
  imports: [
    ReactiveFormsModule,
    RouterLink,
    InputSwitchComponent,
    DropdownSelectComponent,
    DropdownSelectOptionComponent,
    NgOptimizedImage,
    ProductPrioritySelectorComponent
],
  templateUrl: './site-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './site-form.component.scss'
})
@IsGranted(['ROLE_API_SITE_WRITE', 'SITE_VIEW'])
@MetaTitle('Site form')
export class SiteFormComponent implements OnInit {

  loaded = false;
  id: number | null = null;
  site: Site | null = null;
  products: Product[] = [];
  partners: Partner[] = [];
  offers: Offer[] = [];
  countries: Country[] = [];
  languages: Language[] = [];

  form = new FormGroup({
    productId: new FormControl<number | null>(null, Validators.required),
    name: new FormControl('', Validators.required),
    url: new FormControl('', Validators.required),
    gtm: new FormControl('', Validators.maxLength(12)),
    contactEmail: new FormControl(''),
    dpoEmail: new FormControl(''),
    termsUrl: new FormControl<string | null>(null),
    privacyUrl: new FormControl<string | null>(null),
    cookieUrl: new FormControl<string | null>(null),
    contactUrl: new FormControl<string | null>(null),
    domain: new FormControl('', Validators.required),
    companyName: new FormControl(''),
    companyAddress: new FormControl(''),
    companyPostalCode: new FormControl(''),
    companyCountry: new FormControl(''),
    companyRegistrationNumber: new FormControl(''),
    companyEuVatNumber: new FormControl(''),
    companyGbVatNumber: new FormControl(''),
    enabled: new FormControl(true),
    partnerId: new FormControl(),
    offerId: new FormControl(),
    lang: new FormControl<string | null>(null),
    productP2: new FormControl<string | null>(null),
    productP2Alt: new FormControl<string | null>(null),
    productCross: new FormControl<string | null>(null),
    productSlide: new FormControl<string | null>(null),
    productP2Gdpr: new FormControl<string | null>(null),
    productP2AltGdpr: new FormControl<string | null>(null),
    productCrossGdpr: new FormControl<string | null>(null),
    productSlideGdpr: new FormControl<string | null>(null),
  });

  constructor(
    protected countryService: CountryService,
    private loader: LoaderService,
    private snackbar: SnackbarService,
    private route: ActivatedRoute,
    private router: Router,
    private auth: AuthService,
    private siteService: SiteService,
    private partnerService: PartnerService,
    private productService: ProductService,
    private offerService: OfferService,
    protected langService: LangService,
  ) {
  }

  ngOnInit(): void {
    this.id = Number(this.route.snapshot.paramMap.get('id'));

    this.loader.startLoading();
    forkJoin([
      this.productService.findAll({sort: 'name'}),
      this.countryService.findAll(),
      this.partnerService.findAll({sort: 'companyName'}),
      this.offerService.findAll(),
      this.langService.getLanguages()
    ]).subscribe(([products, countries, partners, offers, languages]) => {
      this.products = products;
      this.countries = countries;
      this.partners = partners;
      this.offers = offers;
      this.languages = languages;

      if (this.id) {
        this.siteService.findOneById(this.id, {groups: 'product'}).subscribe((site: Site) => {
          this.site = site;

          this.form.patchValue({
            productId: this.site.product?.id,
            name: this.site.name,
            domain: this.site.domain,
            url: this.site.url,
            gtm: this.site.gtm?.containerId ?? '',
            contactEmail: this.site.contactEmail,
            dpoEmail: this.site.dpoEmail,
            termsUrl: this.site.termsUrl,
            privacyUrl: this.site.privacyUrl,
            cookieUrl: this.site.cookieUrl,
            contactUrl: this.site.contactUrl,
            companyName: this.site.companyName,
            companyAddress: this.site.companyAddress,
            companyPostalCode: this.site.companyPostalCode,
            companyCountry: this.site.companyCountry,
            companyRegistrationNumber: this.site.companyRegistrationNumber,
            companyEuVatNumber: this.site.companyEuVatNumber,
            companyGbVatNumber: this.site.companyGbVatNumber,
            enabled: this.site.enabled,
            partnerId: this.site.partnerId,
            offerId: this.site.offerId,
            lang: this.site.lang,
            productP2: this.site.productP2,
            productP2Alt: this.site.productP2Alt,
            productCross: this.site.productCross,
            productSlide: this.site.productSlide,
            productP2Gdpr: this.site.productP2Gdpr,
            productP2AltGdpr: this.site.productP2AltGdpr,
            productCrossGdpr: this.site.productCrossGdpr,
            productSlideGdpr: this.site.productSlideGdpr,
          });
          this.loader.stopLoading();
          this.loaded = true;
        });
      } else {
        this.loader.stopLoading();
        this.loaded = true;
      }
    });
  }

  onSubmit(): void {
    if (!this.form.valid) {
      return;
    }

    this.loader.startLoading();

    let params: any = this.form.value;

    if (this.isCreate()) {
      this.siteService.create(params).subscribe((response) => {
        this.loader.stopLoading();
        this.snackbar.success('Site has been created.');
        this.router.navigateByUrl('/site/sites/' + response.id).then();
      });
    } else if (this.site?.id) {
      this.siteService.update(this.site.id, params).subscribe(() => {
        this.loader.stopLoading();
        this.snackbar.success('Site has been modified.');
        this.router.navigateByUrl('/site/sites/' + this.site?.id).then();
      });
    }
  }

  isCreate(): boolean {
    return !this.id;
  }

  canUpdate(): boolean {
    return this.auth.hasRolesSync(['ROLE_API_SITE_WRITE']);
  }
}
